import React from 'react';
import { Link } from 'react-router-dom';
import { PiImage } from 'react-icons/pi';
import { HomeTitle } from './HomeSectionHead';
import HomeDarkIsland from './HomeDarkIsland';
import { formatUSD } from '../../utils/money';

export function serviceRateLabel(listing) {
  if (typeof listing.price !== 'number') return null;
  const amount = formatUSD(listing.price);
  if (listing.listingType?.includes('hourly')) return `${amount}/hr`;
  if (listing.listingType?.includes('negotiable')) {
    // Listing has no dedicated max-price field, but tiered packages
    // (Basic/Standard/Premium) each carry their own price — the spread
    // across tiers *is* the negotiable range, so derive it instead of just
    // flooring at the cheapest tier with a "+".
    const tierPrices = (listing.packages || [])
      .map((p) => Number(p?.price))
      .filter((n) => Number.isFinite(n));
    const min = tierPrices.length ? Math.min(...tierPrices) : null;
    const max = tierPrices.length ? Math.max(...tierPrices) : null;
    if (min != null && max != null && max > min) {
      return `${formatUSD(min)}–${formatUSD(max)}`;
    }
    return `${amount}+`;
  }
  return amount;
}

// Light frost. Secondary lines use brass-deep/prose rather than brass-ink/muted
// so every line holds 4.5:1 even with a black photo under the panel.
const GLASS =
  'bg-white/[0.76] backdrop-blur-glass backdrop-saturate-150 shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_8px_24px_-12px_rgba(18,22,28,0.35)]';

const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-press-paper';

const ZOOM =
  'transition-transform duration-700 ease-spring group-hover:scale-[1.035] motion-reduce:transition-none motion-reduce:group-hover:scale-100';

function ownerNameOf(listing) {
  return (
    listing.ownerName ||
    `${listing.owner?.firstName || ''} ${listing.owner?.lastName || ''}`.trim() ||
    'UniFreelancer talent'
  );
}

function NoPhoto() {
  return (
    <div className="absolute inset-0 flex items-center justify-center text-press-muted">
      <PiImage size={28} aria-hidden="true" />
    </div>
  );
}

function GlassMeta({ listing, lead }) {
  const rate = serviceRateLabel(listing);
  return (
    <>
      <div className="flex items-center justify-between gap-2 text-[12px]">
        <span className="truncate font-semibold text-press-brass-deep">{listing.category}</span>
        {listing.reviewCount > 0 && (
          <span className="flex flex-none items-center gap-1 text-press-prose">
            <span className="text-press-brass-ink" aria-hidden="true">★</span>
            <span className="font-semibold text-press-ink">{listing.rating}</span>
            <span>({listing.reviewCount})</span>
          </span>
        )}
      </div>
      <h3
        className={`mt-1 line-clamp-2 font-press-display font-normal leading-[1.25] text-press-ink ${
          lead ? 'text-[22px]' : 'text-[16px]'
        }`}
      >
        {listing.title}
      </h3>
      <div className="mt-1.5 flex items-center gap-2.5">
        <span className="min-w-0 flex-1 truncate text-[12px] text-press-prose">by {ownerNameOf(listing)}</span>
        <span className="flex-none text-[13px] font-semibold tabular-nums text-press-ink">
          {rate || 'Contact for pricing'}
        </span>
      </div>
    </>
  );
}

function SectionHead() {
  return (
    <>
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0 flex-1">
          <HomeTitle dark title="Featured services" desc="Hand-picked work from verified students, ready to order." />
        </div>
        <Link
          to="/hire-talent"
          className="whitespace-nowrap text-[13px] font-semibold text-press-paper/80 transition hover:text-press-paper focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-press-paper"
        >
          Browse services →
        </Link>
      </div>
    </>
  );
}

// Matted plate: the photo is never cropped: it sits whole at the top
// of the card and a blurred copy of itself fills the rest.
function PlateGrid({ listings, loading }) {
  const grid =
    'mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:auto-rows-[260px] lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)]';
  const cell = (lead) =>
    `aspect-[4/5] lg:aspect-auto ${lead ? 'sm:col-span-2 sm:aspect-[16/10] lg:col-span-1 lg:row-span-2' : ''}`;

  if (loading) {
    return (
      <div className={grid}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={`rounded-[14px] bg-white/[0.06] ${cell(i === 0)}`} />
        ))}
      </div>
    );
  }
  return (
    <div className={grid}>
      {listings.map((listing, i) => {
        const cover = listing.photos?.[0];
        const lead = i === 0;
        return (
          <Link
            key={listing._id}
            to={`/hire-talent/listing/${listing._id}`}
            className={`group relative isolate block overflow-hidden rounded-[14px] bg-press-paper-2 ${cell(lead)} ${FOCUS}`}
          >
            {cover ? (
              <>
                <img
                  src={cover}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute -left-[12%] -top-[12%] h-[124%] w-[124%] max-w-none object-cover blur-[28px] brightness-90 saturate-[1.3]"
                />
                <img
                  src={cover}
                  alt=""
                  loading="lazy"
                  className={`absolute inset-0 h-full w-full object-contain object-top ${ZOOM}`}
                />
              </>
            ) : (
              <NoPhoto />
            )}
            <div
              className={`absolute border border-white/65 ${GLASS} ${
                lead
                  ? 'inset-x-3.5 bottom-3.5 rounded-[12px] px-[18px] py-4'
                  : 'inset-x-2.5 bottom-2.5 rounded-[10px] px-3 py-[11px]'
              }`}
            >
              <GlassMeta listing={listing} lead={lead} />
            </div>
          </Link>
        );
      })}
    </div>
  );
}

// Sits in a dark island (no theme-night: the glass slips keep their dark text).
export default function HomeFeaturedServices({ listings, loading }) {
  return (
    <section className="px-3 pb-16 sm:px-6 lg:px-10" aria-label="Featured services">
      <HomeDarkIsland>
        <SectionHead />
        <PlateGrid listings={listings} loading={loading} />
      </HomeDarkIsland>
    </section>
  );
}
